import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, SERVICE_OPTIONS, SITE, VERTICALS } from "@/constants/site";

const inputCls =
  "w-full px-4 py-3 rounded bg-[#0c1427] border border-[#203154] text-white text-sm placeholder-[#4b658d] focus:border-[#38bdf8] focus:outline-none transition-colors";

export default function Services() {
  const { hash } = useLocation();
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    service: SERVICE_OPTIONS[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (hash === "#contact") {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setSubmitted(true);
      toast.success("Enquiry submitted — our leadership team will respond shortly.");
      setForm({ company: "", contact_name: "", email: "", service: SERVICE_OPTIONS[0], message: "" });
    } catch (err) {
      toast.error("Could not submit right now. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="services-page">
      {/* PAGE HERO */}
      <section className="py-20 md:py-28 bg-[#0c1427] border-b border-[#203154]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162544] border border-[#203154] text-[#38bdf8] font-mono text-xs uppercase tracking-widest mb-8 d3-reveal">
            Enterprise Gateway · Delivery Partner
          </div>
          <h1 className="font-heavy text-[clamp(38px,5vw,72px)] uppercase text-white leading-[0.92] tracking-tight mb-6">
            <span className="ln"><span>Three High-Impact</span></span>
            <span className="ln"><span className="text-[#38bdf8]">Verticals.</span></span>
          </h1>
          <p className="text-base md:text-lg text-[#9cb4d8] leading-relaxed max-w-2xl d3-reveal">
            A trusted IT delivery partner for enterprises — from turnkey product engineering to
            managed services and corporate upskilling.
          </p>
        </div>
      </section>

      {/* VERTICALS DETAIL */}
      <section className="py-24 md:py-32 bg-[#101b33] border-b border-[#203154]" data-testid="verticals-detail-section">
        <div className="max-w-6xl mx-auto px-6 space-y-6">
          {VERTICALS.map((v) => (
            <div
              key={v.num}
              className="p-8 md:p-10 rounded-xl bg-[#0c1427] border border-[#203154] grid grid-cols-1 md:grid-cols-12 gap-6 items-start"
              data-testid={`service-detail-${v.num}`}
            >
              <div className="md:col-span-2 text-3xl font-bold font-mono text-[#38bdf8]">{v.num}</div>
              <div className="md:col-span-7">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-3">{v.title}</h2>
                <p className="text-sm text-[#9cb4d8] leading-relaxed">{v.blurb}</p>
              </div>
              <div className="md:col-span-3 md:text-right">
                <a
                  href="#contact"
                  data-testid={`service-cta-${v.num}`}
                  className="inline-block py-2.5 px-5 rounded bg-[#203154] text-white text-xs font-bold font-mono uppercase tracking-wider hover:bg-[#38bdf8] hover:text-[#0c1427] transition-colors"
                >
                  Enquire →
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT + ENQUIRY */}
      <section id="contact" className="py-24 md:py-32 bg-[#0c1427]" data-testid="contact-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Info */}
            <div className="lg:col-span-5 min-w-0">
              <div className="text-xs font-mono uppercase tracking-widest text-[#38bdf8] mb-3">
                Enterprise Gateway
              </div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">
                Let's Discuss Your Project
              </h2>
              <p className="text-sm text-[#9cb4d8] leading-relaxed mb-8">
                Reach out to our leadership team for custom software scopes, corporate batch
                training, or managed IT resource contracting.
              </p>

              <div className="space-y-4 text-xs font-mono text-[#9cb4d8]">
                <div className="p-4 rounded-lg bg-[#162544] border border-[#203154]" data-testid="contact-address-card">
                  <div className="text-white uppercase mb-1">Registered Address</div>
                  <div className="text-[#9cb4d8] font-sans">{SITE.address}</div>
                </div>
                <div className="p-4 rounded-lg bg-[#162544] border border-[#203154] flex items-center justify-between" data-testid="contact-phone-card">
                  <span className="text-white uppercase">Phone</span>
                  <a href={SITE.phoneHref} className="text-[#38bdf8] hover:text-white transition-colors" data-testid="contact-phone-link">
                    {SITE.phone}
                  </a>
                </div>
                <div className="p-4 rounded-lg bg-[#162544] border border-[#203154] flex items-center justify-between" data-testid="contact-email-card">
                  <span className="text-white uppercase">Email</span>
                  <a href={`mailto:${SITE.email}`} className="text-[#38bdf8] hover:text-white transition-colors" data-testid="contact-email-link">
                    {SITE.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7 min-w-0">
              <div className="p-8 rounded-xl bg-[#162544] border border-[#203154]">
                <h3 className="text-2xl font-bold text-white mb-2">Corporate Service Enquiry</h3>
                <p className="text-xs text-[#9cb4d8] mb-6">
                  Fill the form below to receive a consultation proposal.
                </p>

                {submitted ? (
                  <div
                    className="p-6 rounded-lg bg-[#0c1427] border border-[#38bdf8]/40 text-center"
                    data-testid="enquiry-success-message"
                  >
                    <p className="text-sm text-white font-bold mb-2">Thank you — enquiry received.</p>
                    <p className="text-xs text-[#9cb4d8] mb-4">
                      Our leadership team will respond with a consultation proposal shortly.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      data-testid="enquiry-submit-another-button"
                      className="text-xs font-mono uppercase text-[#38bdf8] hover:text-white transition-colors"
                    >
                      Submit another →
                    </button>
                  </div>
                ) : (
                  <form className="space-y-4" onSubmit={onSubmit} data-testid="enquiry-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="enquiry-company" className="block text-xs font-mono text-[#9cb4d8] uppercase mb-1">
                          Company Name
                        </label>
                        <input
                          id="enquiry-company"
                          type="text"
                          name="company"
                          value={form.company}
                          onChange={onChange}
                          placeholder="Company Name"
                          required
                          className={inputCls}
                          data-testid="enquiry-company-input"
                        />
                      </div>
                      <div>
                        <label htmlFor="enquiry-name" className="block text-xs font-mono text-[#9cb4d8] uppercase mb-1">
                          Your Name
                        </label>
                        <input
                          id="enquiry-name"
                          type="text"
                          name="contact_name"
                          value={form.contact_name}
                          onChange={onChange}
                          placeholder="Your Name"
                          required
                          className={inputCls}
                          data-testid="enquiry-name-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="enquiry-email" className="block text-xs font-mono text-[#9cb4d8] uppercase mb-1">
                          Work Email
                        </label>
                        <input
                          id="enquiry-email"
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={onChange}
                          placeholder="name@company.com"
                          required
                          className={inputCls}
                          data-testid="enquiry-email-input"
                        />
                      </div>
                      <div>
                        <label htmlFor="enquiry-service" className="block text-xs font-mono text-[#9cb4d8] uppercase mb-1">
                          Service Needed
                        </label>
                        <select
                          id="enquiry-service"
                          name="service"
                          value={form.service}
                          onChange={onChange}
                          className={inputCls}
                          data-testid="enquiry-service-select"
                        >
                          {SERVICE_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="enquiry-message" className="block text-xs font-mono text-[#9cb4d8] uppercase mb-1">
                        Requirements Overview
                      </label>
                      <textarea
                        id="enquiry-message"
                        name="message"
                        rows={3}
                        value={form.message}
                        onChange={onChange}
                        placeholder="Provide details on project goals, tech stack, or scope..."
                        className={inputCls}
                        data-testid="enquiry-message-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="enquiry-submit-button"
                      className="w-full py-3.5 rounded bg-[#38bdf8] text-[#0c1427] font-bold text-xs font-mono uppercase tracking-wider hover:bg-white transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Submit Enquiry"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
